import { useEffect, useState } from 'react';
import type { EntityType } from '@erp-attach/shared';
import type { DocumentSummary, FolderNode } from '../api/types';
import { dropVerdict, findFolderIn } from '../lib/fileActions';
import { visibleFolders } from './FolderTree';
import { Button } from './ui/Button';
import { Dialog } from './ui/Dialog';
import { ErrorBanner } from './ui/ErrorBanner';
import { Icon } from './ui/Icon';

function flatten(list: readonly FolderNode[], depth = 0): { folder: FolderNode; depth: number }[] {
  return list.flatMap((f) => [{ folder: f, depth }, ...flatten(f.children, depth + 1)]);
}

/**
 * The keyboard and touch path to the move API. Ineligible folders stay visible, disabled, with the
 * reason, so people learn why a drop is refused.
 */
export function MoveDialog({
  doc,
  folders,
  entityType,
  open,
  onClose,
  onSubmit,
}: {
  doc: DocumentSummary;
  folders: readonly FolderNode[];
  entityType: EntityType;
  open: boolean;
  onClose: () => void;
  onSubmit: (folder: FolderNode) => Promise<unknown>;
}) {
  const source = findFolderIn(folders, doc.folderId);
  const [choice, setChoice] = useState<FolderNode | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!open) return;
    setChoice(null);
    setError(null);
  }, [open]);

  const submit = async () => {
    if (!choice) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(choice);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title={`Move ${doc.title}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!choice} busy={busy} onClick={() => void submit()}>
            Move
          </Button>
        </>
      }
    >
      <fieldset>
        <legend className="sr-only">Destination folder</legend>
        <ul className="max-h-80 space-y-0.5 overflow-auto">
          {flatten(visibleFolders(folders, entityType)).map(({ folder, depth }) => {
            const verdict = dropVerdict(folder, doc, source);
            return (
              <li key={folder.id} style={{ paddingLeft: `${depth * 0.9}rem` }}>
                <label className={`flex items-center gap-2 rounded px-2 py-1.5 ${verdict.ok ? 'cursor-pointer hover:bg-[var(--selection)]' : 'opacity-60'}`}>
                  <input type="radio" name="move-target" disabled={!verdict.ok} checked={choice?.id === folder.id} onChange={() => setChoice(folder)} />
                  <Icon name="folder" size={15} className="text-muted-foreground" />
                  <span>{folder.name}</span>
                  {!verdict.ok ? <span className="ml-auto text-xs text-muted-foreground">{verdict.reason}</span> : null}
                </label>
              </li>
            );
          })}
        </ul>
      </fieldset>
      {error ? (
        <div className="mt-2">
          <ErrorBanner error={error} />
        </div>
      ) : null}
    </Dialog>
  );
}
